'use client';

import { FC } from 'react';
import { Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { Search, Check } from 'lucide-react';

// Props for `TalentFeatureCallout`.
export type TalentFeatureCalloutProps =
  SliceComponentProps<Content.TalentFeatureCalloutSlice>;

// Component for "TalentFeatureCallout" Slices.
const TalentFeatureCallout: FC<TalentFeatureCalloutProps> = ({ slice }) => {
  const {
    title,
    description,
    cta_link,
    profiles,
    tool_title,
    tool_placeholder,
    technologies,
    tool_cta_link,
    tool_note,
  } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#0F1111] text-white py-20 md:py-32"
    >
      <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left Column */}
        <div className="flex flex-col gap-8">
          <div className="max-w-md">
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-4xl md:text-5xl font-bold mb-4">
                    {children}
                  </h2>
                ),
              }}
            />
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-lg text-white/80">{children}</p>
                ),
              }}
            />
          </div>
          <PrismicNextLink field={cta_link} className="secondary-button">
            Get Top Talent
          </PrismicNextLink>
          <div className="flex gap-4 mt-4">
            {(profiles || []).map((item, index) => (
              <PrismicNextImage
                key={index}
                field={item.profile_image}
                className="w-20 h-20 rounded-lg object-cover"
                fallbackAlt=""
              />
            ))}
          </div>
        </div>

        {/* Right Column (Tool Card) */}
        <div className="bg-[#1A1A1A] p-8 rounded-2xl flex flex-col gap-6 border border-white/10">
          <h3 className="text-sm font-bold tracking-widest text-white/70">
            {tool_title}
          </h3>

          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder={tool_placeholder || ''}
              className="w-full bg-white text-black rounded-lg py-3 px-4 focus:outline-none focus:ring-2 focus:ring-green-400"
            />
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          {/* Technologies Checklist */}
          <div>
            <p className="text-white/80 mb-4">Technologies</p>
            <div className="grid grid-cols-2 gap-4">
              {(technologies || []).map((item, index) => (
                <label
                  key={index}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <div className="w-5 h-5 bg-white border-2 border-white/50 rounded-sm flex items-center justify-center">
                    {/* The Prismic model for this slice does not include a boolean for the checked state. */}
                    {/* I'm mocking the checked state for visual representation based on the screenshot. */}
                    {(item.technology_name === 'React' ||
                      item.technology_name === 'AWS') && (
                      <Check className="text-green-400" />
                    )}
                  </div>
                  <span className="text-white/90">{item.technology_name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Tool CTA */}
          <PrismicNextLink
            field={tool_cta_link}
            className="secondary-button w-full text-center"
          >
            Build Top Team
          </PrismicNextLink>

          {/* Tool Note */}
          <div className="text-sm text-green-400 text-center">
            <PrismicRichText field={tool_note} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default TalentFeatureCallout;
